import { useSyncExternalStore } from 'react'
import { formatAge, formatTime } from '@/lib/format'

/**
 * One clock for every age on the page: it ticks once a second while any cell listens and stops when the last one
 * leaves. Each cell reads its own label off it, so React repaints a cell only on the second its label changes (every
 * second under a minute, once a minute after that, once an hour after that).
 */
const listeners = new Set<() => void>()
let timer: ReturnType<typeof setInterval> | null = null
let now = Date.now()

function tick() {
  now = Date.now()
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) {
    now = Date.now()
    timer = setInterval(tick, 1000)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer)
      timer = null
    }
  }
}

/** the clock's reading: the last tick while it runs, the moment itself before the first cell subscribes */
const current = () => (timer === null ? Date.now() : now)

/** how long ago `at` was, counting up live (John, Nov 3 2026: "30s -> 31s -> 32s"); the exact time sits in the tooltip */
export function Age({ at, className }: { at: string | null | undefined; className?: string }) {
  const label = useSyncExternalStore(subscribe, () => formatAge(at, current()))
  return (
    <time dateTime={at ?? undefined} title={at ? formatTime(at) : undefined} className={className}>
      {label}
    </time>
  )
}
